// Hook custom per recuperare articoli del blog di un'agenzia con paginazione.
// Nota: uso axios e TypeScript per tipizzare la risposta, gestire errori, loading e paginazione.

import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione articolo del blog
export interface ArticoloBlog {
    id: number;
    agencyId: number;
    categoriaId: number | null;
    foto: string | null;
    titolo: string;
    testoRidotto: string | null;
    createdAt: string;
    modifyAt: string | null;
    web: boolean;
    homepage: boolean;
    propertyId: number | null;
    seoString: string | null;
    portalId: number | null;
    dataPubblicazione: string;
    nomeCategoria: string | null;
    categoriaSeoString: string | null;
}

// Tipizzazione dati di paginazione
interface PaginationData {
    currentPage: number;
    totalItems: number;
    totalPages: number;
    limit: number;
}

// Tipizzazione del risultato dell'hook
interface UseArticoliResult {
    articles: ArticoloBlog[];
    isLoading: boolean;
    error: string | null;
    pagination: PaginationData;
    fetchArticoli: (page: number) => void;
}

// Parametri dell'hook
interface UseArticoliOptions {
    agencyId: number;
    initialLimit?: number;
    initialFilters?: Record<string, any>;
    initialOrderBy?: { column: string; direction: 'asc' | 'desc' };
}

/**
 * useArticoli
 * @param agencyId - ID dell'agenzia
 * @param initialLimit - numero di articoli per pagina (default 3)
 * @param initialFilters - eventuali filtri da applicare
 * @param initialOrderBy - ordinamento iniziale
 *
 * Nota per il prossimo dev:
 * - `articles` contiene gli articoli correnti
 * - `isLoading` true mentre la chiamata è in corso
 * - `error` contiene il messaggio di errore
 * - `pagination` contiene info su pagina corrente, totale articoli e totale pagine
 * - `fetchArticoli(page)` permette di richiamare manualmente la pagina desiderata
 */
const useArticoli = ({
    agencyId,
    initialLimit = 3,
    initialFilters = {},
    initialOrderBy = { column: 'dataPubblicazione', direction: 'desc' }
}: UseArticoliOptions): UseArticoliResult => {
    const [articles, setArticles] = useState<ArticoloBlog[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState<PaginationData>({
        currentPage: 1,
        totalItems: 0,
        totalPages: 0,
        limit: initialLimit,
    });
    const [filters] = useState(initialFilters);
    const [orderBy] = useState(initialOrderBy);

    // Funzione per fetch degli articoli
    const fetchArticoli = useCallback(async (page: number = 1) => {
        setIsLoading(true);
        setError(null);
        console.log(`Fetching articles for page: ${page}`);

        const offset = (page - 1) * pagination.limit;

        try {
            const response = await axios.post(`${API_BASE_URL}/blog/articoli`, {
                agencyId,
                limit: pagination.limit,
                withCount: true,
                offset,
            });

            if (response.data && response.data.data) {
                setArticles(response.data.data);
                const totalItems = response.data.count || 0;
                const totalPages = Math.ceil(totalItems / pagination.limit);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalItems,
                    totalPages,
                }));
            } else {
                setArticles([]);
                setPagination(prev => ({
                    ...prev,
                    currentPage: page,
                    totalItems: 0,
                    totalPages: 0,
                }));
                console.warn("API response successful but data array is missing or empty.");
            }

        } catch (err: any) {
            console.error("Error fetching articles:", err);
            const errorMessage = err.response?.data?.error || err.message || "Errore durante il recupero degli articoli.";
            setError(errorMessage);
            setArticles([]);
            setPagination(prev => ({
                ...prev,
                currentPage: page,
                totalItems: 0,
                totalPages: 0,
            }));
        } finally {
            setIsLoading(false);
        }
    }, [agencyId, pagination.limit]);

    // Effetto iniziale e aggiornamento quando cambiano agencyId, filtri, ordinamento o limit
    useEffect(() => {
        fetchArticoli(1);
    }, [agencyId, pagination.limit, filters, orderBy, fetchArticoli]);

    return {
        articles,
        isLoading,
        error,
        pagination,
        fetchArticoli,
    };
};

export default useArticoli;